/** Thin JSON client for consortium-api (cookie session via credentials). */

export type MeResponse = {
  authenticated: boolean;
  member_id?: string;
  partner_id?: string;
  login_name?: string;
  display_name?: string;
  partner_display_name?: string;
  note?: string;
};

export type DropItem = {
  drop_id: string;
  published_at: string | null;
  system_name: string | null;
  item_counts: Record<string, unknown>;
};

export type HullReplacement = {
  date: string;
  label: string;
  part_number: string;
};

export type HullCard = {
  identifier_value: string;
  identifier_type: string | null;
  system_name: string;
  brand: string | null;
  hull_id: string | null;
  hull_number: string | null;
  platform_id: string | null;
  platform_label: string | null;
  platform_version: string | null;
  observed_at: string | null;
  lifecycle: string | null;
  design_category: string | null;
  build_sheet: string[];
  config_frozen_at: string | null;
  last_service_at: string | null;
  last_service_label: string | null;
  replacements: HullReplacement[];
  campaign: string | null;
};

async function api<T>(
  path: string,
  init?: RequestInit,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
    ...init,
  });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = { error: text };
    }
  }
  if (!res.ok) {
    const err =
      body &&
      typeof body === "object" &&
      "error" in body &&
      typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : `http_${res.status}`;
    return { ok: false, status: res.status, error: err };
  }
  return { ok: true, data: body as T };
}

export function fetchMe() {
  return api<MeResponse>("/api/v1/me");
}

export function login(loginName: string, password: string) {
  return api<MeResponse>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ login: loginName, password }),
  });
}

export function logout() {
  return api<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST" });
}

export function forgotPassword(loginName: string) {
  return api<{ ok: boolean }>("/api/v1/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ login: loginName }),
  });
}

export function setPassword(input: {
  userId: string;
  code: string;
  password: string;
  mode?: "invite" | "reset";
}) {
  return api<{ ok: boolean }>("/api/v1/auth/set-password", {
    method: "POST",
    body: JSON.stringify({
      userId: input.userId,
      code: input.code,
      password: input.password,
      mode: input.mode ?? "invite",
    }),
  });
}

export function fetchDrops(limit = 20, offset = 0) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return api<{ items: DropItem[]; total_count: number }>(
    `/api/v1/drops?${params}`,
  );
}

export function findHulls(q: string, limit = 20) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return api<{ items: HullCard[] }>(`/api/v1/hulls/find?${params}`);
}

export type CatalogHull = {
  identifier_value: string;
  identifier_type: string | null;
  system_name: string;
  hull_number: string | null;
  platform_id: string | null;
  platform_version: string | null;
};

export type CatalogItem = {
  part_number: string;
  name: string | null;
  mg: number | null;
  sg: number | null;
  in_as_built: boolean;
  qty: number | null;
  offer?: OfferItem | null;
  catalog_role?: string | null;
  superseded?: boolean;
  successor_part_number?: string | null;
  option_codes?: string[];
};

export type OfferItem = {
  part_number: string;
  system_name: string;
  unit_price: string | null;
  currency: string | null;
  stock_band: string | null;
  eta_hint: string | null;
  can_add_to_cart: boolean;
};

export type PartMeta = {
  part_number: string;
  name: string | null;
  mg: number | null;
  sg: number | null;
  part_type: string | null;
  lifecycle_state: string | null;
};

export type PartLookupResponse = {
  query: string;
  part: PartMeta | null;
  offers: OfferItem[];
  matches: PartMeta[];
};

export function lookupPart(pn: string, yard = "") {
  const params = new URLSearchParams({ pn: pn.trim() });
  if (yard.trim()) params.set("yard", yard.trim());
  return api<PartLookupResponse>(`/api/v1/parts?${params}`);
}

export function fetchOffers(pn: string, yard = "") {
  const params = new URLSearchParams({ pn: pn.trim() });
  if (yard.trim()) params.set("yard", yard.trim());
  return api<{ items: OfferItem[] }>(`/api/v1/offers?${params}`);
}

export type CatalogResponse = {
  hull: CatalogHull;
  items: CatalogItem[];
};

export function fetchCatalog(win: string, q = "", limit = 100) {
  const params = new URLSearchParams({
    win,
    limit: String(limit),
  });
  if (q.trim()) params.set("q", q.trim());
  return api<CatalogResponse>(`/api/v1/catalog?${params}`);
}

export type CatalogYard = {
  system_name: string;
  display_name: string;
  platform_count: number;
  hull_count: number;
};

export type CatalogPlatform = {
  platform_id: string;
  platform_version: string | null;
  display_name: string;
  hull_count: number;
};

export type PlatformCatalogResponse = {
  system_name: string;
  platform_id: string;
  platform_version: string | null;
  items: CatalogItem[];
  hull?: CatalogHull | null;
};

export function fetchCatalogYards() {
  return api<{ items: CatalogYard[] }>("/api/v1/catalog/yards");
}

export function fetchCatalogPlatforms(yard: string) {
  const params = new URLSearchParams({ yard });
  return api<{ system_name: string; items: CatalogPlatform[] }>(
    `/api/v1/catalog/platforms?${params}`,
  );
}

export function fetchPlatformCatalog(
  yard: string,
  platform: string,
  q = "",
  win = "",
) {
  const params = new URLSearchParams({
    yard,
    platform,
    limit: "500",
  });
  if (q.trim()) params.set("q", q.trim());
  if (win.trim()) params.set("win", win.trim());
  return api<PlatformCatalogResponse>(`/api/v1/catalog/platform?${params}`);
}

export type CatalogSchematicHotspot = {
  mark: string;
  part_number?: string | null;
  anchor_x: number;
  anchor_y: number;
  label_x: number;
  label_y: number;
};

export type PlatformSchematicResponse = {
  system_name: string;
  platform_id: string;
  plate_id: string;
  mg: number;
  sg: number;
  svg: string;
  hotspots: CatalogSchematicHotspot[];
};

export function fetchPlatformSchematic(
  yard: string,
  platform: string,
  mg: number,
  sg: number,
) {
  const params = new URLSearchParams({
    yard,
    platform,
    mg: String(mg),
    sg: String(sg),
  });
  return api<PlatformSchematicResponse>(
    `/api/v1/catalog/platform/schematic?${params}`,
  );
}

export type CartLine = {
  line_id: string;
  part_number: string;
  system_name: string;
  qty: number;
  unit_price: string | null;
  currency: string | null;
};

export type CartResponse = {
  cart_id: string;
  status: string;
  lines: CartLine[];
};

export function fetchCart() {
  return api<CartResponse>("/api/v1/cart");
}

export function addCartLine(input: {
  part_number: string;
  system_name: string;
  qty?: number;
}) {
  return api<CartResponse>("/api/v1/cart/lines", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function removeCartLine(lineId: string) {
  return api<CartResponse>(`/api/v1/cart/lines/${lineId}`, {
    method: "DELETE",
  });
}

export function submitCart() {
  return api<{
    intake_id: string;
    cart_id: string;
    system_name: string;
    status: string;
  }>("/api/v1/cart/submit", { method: "POST" });
}

export type IntakeItem = {
  intake_id: string;
  cart_id: string;
  system_name: string;
  status: string;
  created_at: string;
  yard_order_id?: string | null;
  status_note?: string | null;
  status_lines?: Array<{
    part_number?: string;
    qty_requested?: number;
    qty_confirmed?: number;
    promised_eta?: string;
    confirmation?: string;
    fulfillment_mode?: string;
    reason?: string;
  }> | null;
};

export function fetchIntakes() {
  return api<{ items: IntakeItem[] }>("/api/v1/intakes");
}
